import { Check, Sparkles } from "lucide-react";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import { Button } from "@/components/ui/button";
import { Pill } from "@/components/ui/pill";
import {
  fmtMetric,
  meetsObjectiveTarget,
  objDelta,
  objMetric,
} from "@/features/simulations/auto/metrics";

interface Props {
  batch: AutoBatchResponse;
  baseline: AutoCandidate | null;
  ranked: AutoCandidate[];
  onApply: (c: AutoCandidate) => void;
}

// As variações chegam DEPOIS da resposta, na mesma faixa: "uma ação, duas entregas".
// Cada card exibe a métrica do objetivo escolhido (nunca OEE fixo), porque o que é "melhor" muda
// com a pergunta — num objetivo de prazo, mais folga; num de tempo, menos horas.
export function VariationCards({ batch, baseline, ranked, onApply }: Props) {
  if (ranked.length === 0) return null;

  return (
    <div className="space-y-2 border-t pt-3">
      <p className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        <Sparkles className="h-3.5 w-3.5" aria-hidden />
        {ranked.length} ajuste{ranked.length > 1 ? "s" : ""} sugerido
        {ranked.length > 1 ? "s" : ""}
      </p>

      <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
        {ranked.map((c, i) => {
          const metric = objMetric(c, batch);
          const delta = objDelta(c, baseline, batch);
          const meets = meetsObjectiveTarget(c, batch);
          return (
            <div key={c.simulationId} className="rounded-md border bg-card p-3">
              <div className="flex items-center gap-2">
                <span className="font-mono text-[11px] text-muted-foreground">
                  #{c.rankPosition ?? i + 1}
                </span>
                <span className="flex-1 truncate text-[13px] font-medium" title={c.label ?? ""}>
                  {c.label ?? "Ajuste"}
                </span>
                {meets === true ? (
                  <Pill tone="success" withDot={false} icon={<Check className="h-3 w-3" />}>
                    Cabe
                  </Pill>
                ) : meets === false ? (
                  <Pill tone="warning" withDot={false}>
                    Não cabe
                  </Pill>
                ) : null}
              </div>

              <div className="mt-2 flex items-baseline gap-2">
                <span className="font-mono text-[18px] font-semibold">{fmtMetric(metric)}</span>
                {delta ? (
                  <span
                    className={
                      delta.positive
                        ? "text-[12px] font-medium text-emerald-600 dark:text-emerald-400"
                        : "text-[12px] font-medium text-destructive"
                    }
                  >
                    {delta.text}
                  </span>
                ) : null}
              </div>

              <Button
                variant="outline"
                size="sm"
                className="mt-2.5 w-full"
                onClick={() => onApply(c)}
              >
                Aplicar ao rascunho
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
